import React, { Children, useState, useCallback, useEffect } from 'react'
import cx from 'classnames'
import styles from './RadioGroup.module.scss'
import Radio from '../../atoms/Radio/Radio'
import { Tooltip } from 'views/components'
import Info from '../../atoms/Svgs/Info'
import { useHover } from 'react-use'
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  children: React.ReactNode
  className?: string
  defaultSelected?: string | number
  label?: React.ReactNode
  labelHint?: string
  stackRadios?: boolean
  error?: string
}

const RadioGroup = ({
  children,
  className,
  defaultSelected,
  label,
  labelHint,
  onChange = () => {},
  stackRadios,
  error,
  ...props
}: Props) => {
  const { name } = props
  const [selected, setSelected] = useState(defaultSelected)

  // Keep selection in sync when the parent changes defaultSelected
  // programmatically (e.g. autofilling from a selected contact).
  useEffect(() => {
    setSelected(defaultSelected)
  }, [defaultSelected])

  const handleChange = useCallback(
    e => {
      if (e.target.id !== selected) {
        setSelected(e.target.id)
        if (onChange) onChange(e)
      }
    },
    [onChange, selected],
  )

  const hasError = error && error.length > 0

  const classes = cx([styles.root, className], {
    [styles['is-stacked']]: stackRadios,
    [styles['has-error']]: hasError,
  })

  const [hoverableInfoIcon, isHovering] = useHover(
    <div className={styles['info-icon']}>
      <Info width={20} height={20} />
    </div>,
  )

  return (
    <div className={classes}>
      {label && (
        <label>
          {label}
          {hasError && ` - ${error as string}`}
          {labelHint && (
            <Tooltip isOpen={isHovering} body={labelHint}>
              {hoverableInfoIcon}
            </Tooltip>
          )}
        </label>
      )}
      <div className={styles.radios}>
        {Children.map(children, (radioButton: any) => {
          const { value, ...other } = radioButton.props
          return (
            <Radio
              {...other}
              name={name}
              onChange={handleChange}
              id={value}
              key={value}
              value={value}
              checked={value === selected}
              disabled={props.disabled}
            />
          )
        })}
      </div>
    </div>
  )
}

export default RadioGroup
